import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  maxGuessesFor, hintPosition, insightFor,
  type GameState, type Language, type WordInsight,
} from '@lexicon/core';
import { Board } from '../components/Board';
import { FooterActions } from '../components/FooterActions';
import { HintBanner } from '../components/HintBanner';
import { InsightSheet } from '../components/InsightSheet';
import { Keyboard } from '../components/Keyboard';
import { ResultSheet } from '../components/ResultSheet';
import { Toast } from '../components/Toast';
import { finalizeGame } from '../game/finalize';
import { saveSession, toSession, type GameResult } from '../game/session';
import { useGame } from '../game/useGame';
import { hapticInvalid, hapticWin } from '../lib/haptics';
import { shareResult, shareText } from '../lib/share';
import { useApp } from '../state/AppContext';
import { useShell } from '../state/ShellContext';

export interface GamePlayProps {
  /** `String(dayNumber)` for dailies, the challenge code for challenges. */
  id: string;
  /** Referentially stable game state from the screen's loader. */
  initial: GameState;
  initialResult: GameResult | null;
  /**
   * Words already paid for in a restored session for this game (spec §3.7:
   * re-reading an already-revealed insight in the same game is free).
   */
  initialRevealedInsights?: readonly string[];
  /** Required for dailies. */
  dayNumber?: number;
  /**
   * Accepted-guess check for this game's length. Omitted means core's
   * bundled 5-letter list; `null` means this length's list is still
   * loading, and Enter will say so instead of rejecting the word.
   */
  isValid?: ((word: string) => boolean) | null;
  /** Called when a submit was blocked by a still-loading word list. */
  onValidationPending?: () => void;
  banner: (state: { finished: boolean; openResult: () => void }) => ReactNode;
  /** Challenge screens post the outcome from here. */
  onFinalized?: (result: GameResult, game: GameState) => void;
  /** `dayNumber` for dailies, the code for challenges. */
  shareId: string | number;
  ctaLabel: string;
  onCta: () => void;
  resultFooter?: ReactNode;
  /** Extra block under the result subtitle (journey rung progress). */
  resultBanner?: ReactNode;
  /**
   * The language of the *word on the board*, when it differs from the
   * language the app is set to — a challenge link is the only case
   * (spec §4.2: a code carries a word, and its creator may have been
   * playing the other language). It decides which dictionary explains the
   * answer and the guesses.
   *
   * It is deliberately not the language a finished game is *recorded*
   * under: the XP and stats belong to the player's current language,
   * which is the one whose screen they are looking at.
   */
  wordLanguage?: Language;
}

export function GamePlay({
  id, initial, initialResult, initialRevealedInsights = [], dayNumber, isValid, onValidationPending, banner,
  onFinalized, shareId, ctaLabel, onCta, resultFooter, resultBanner, wordLanguage,
}: GamePlayProps) {
  const { storage, refresh, quotas, spendHint, spendInsight, language, t } = useApp();
  // Everything about the *words* reads this; everything about the
  // *player's records* reads `language` (see `wordLanguage` above).
  const words = wordLanguage ?? language;
  const { openMenu } = useShell();
  // Mirrors `quotas` synchronously so spend-gating checks (I1, and the
  // hint-quota guard below) always read the latest value without needing
  // `quotas` itself in a callback's dependency array.
  const quotasRef = useRef(quotas);
  quotasRef.current = quotas;
  const [result, setResult] = useState<GameResult | null>(initialResult);
  const [resultOpen, setResultOpen] = useState(initialResult !== null);

  // Keyed by every word this game has ever paid for — never just the ones
  // whose definition happens to be resolved right now. A word's *presence*
  // as a key is the source of truth for "already revealed" (and so for
  // `persist` below and for `handleReveal`'s no-double-charge guard); a
  // `null` value means its insight chunk was not available at boot (F2
  // review fix — the old version dropped those words here entirely, which
  // both under-persisted the session and let a re-open re-charge them).
  const [revealedInsights, setRevealedInsights] = useState<Record<string, WordInsight | null>>(() => {
    const restored: Record<string, WordInsight | null> = {};
    for (const word of initialRevealedInsights) {
      restored[word] = insightFor(word, words) ?? null;
    }
    return restored;
  });

  const persist = useCallback((game: GameState) => {
    // Carries the current revealedInsights forward so a guess or hint made
    // after an earlier reveal (same game, still in progress) doesn't wipe
    // it out of storage (I4).
    void saveSession(
      storage,
      toSession(id, game, null, Object.keys(revealedInsights)),
      language,
    ).catch(() => {});
  }, [storage, id, revealedInsights, language]);

  const handleFinished = useCallback((game: GameState) => {
    void (async () => {
      const session = await finalizeGame({ storage, id, state: game, dayNumber, language });
      setResult(session.result);
      setResultOpen(true);
      await refresh();
      if (session.result) onFinalized?.(session.result, game);
    })();
  }, [storage, id, dayNumber, refresh, onFinalized, language]);

  const { view, keys, canType, typeLetter, backspace, submit, revealHint, showToast } = useGame({
    initial,
    isValid,
    onValidationPending,
    onGuess: persist,
    onHintApplied: persist,
    onFinished: handleFinished,
    onInvalid: hapticInvalid,
    onWin: hapticWin,
  });

  const openResult = useCallback(() => setResultOpen(true), []);

  const handleShare = useCallback(() => {
    void (async () => {
      const outcome = await shareResult(shareText({
        mode: view.game.mode,
        id: shareId,
        guesses: view.game.guesses,
        won: view.game.status === 'won',
        length: view.game.answer.length,
        language: words,
      }));
      if (outcome === 'copied') showToast(t.common.copied);
      if (outcome === 'failed') showToast(t.common.shareFailed);
    })();
  }, [view.game, shareId, showToast, words, t]);

  const [insightOpen, setInsightOpen] = useState(false);

  // Guards the Hint button against a same-tick double-tap: `hintSpending`
  // folds into `hintDisabled` below so the button's native `disabled`
  // attribute flips the instant a spend is queued, and `hintSpendingRef`
  // backstops it synchronously in case two clicks land before that render
  // commits (see AppContext's spendQuota for the underlying quota race fix).
  const [hintSpending, setHintSpending] = useState(false);
  const hintSpendingRef = useRef(false);

  const handleHint = useCallback(() => {
    if (hintSpendingRef.current) return;
    // A hint must never mutate the game (and incur its XP penalty) once the
    // daily budget is gone — parity with handleReveal's quota gate below.
    // The disabled button already stops this in normal use; this backstops
    // it defensively.
    if (quotasRef.current.hintsLeft <= 0) return;
    // I3: mid-reveal-animation, `revealHint()` correctly refuses (input
    // isn't accepted yet), but that is a different reason than "nothing
    // left to hint" — showing the same toast for both was misleading and
    // silently dropped a legitimate tap. The button disables for the
    // animation's duration (see `hintDisabled` below); this covers a tap
    // that still lands in that same tick.
    if (view.revealingRow !== null) return;
    if (!revealHint()) {
      if (hintPosition(view.game) === null) {
        showToast(t.hint.allRevealed);
      }
      return;
    }
    hintSpendingRef.current = true;
    setHintSpending(true);
    void spendHint().catch(() => {}).finally(() => {
      hintSpendingRef.current = false;
      setHintSpending(false);
    });
  }, [revealHint, showToast, spendHint, view.revealingRow, view.game, t]);

  const guessedWords = useMemo(
    () => [...new Set(view.game.guesses.map((g) => g.word))],
    [view.game.guesses],
  );

  // Same guard as the Hint button, applied per word: `spendingWords` folds
  // into each Reveal button's `disabled` state so the specific button that
  // was just clicked flips off immediately, and `spendingWordsRef` backstops
  // it synchronously in case two clicks on the same word land before that
  // render commits — otherwise a same-tick double-click could charge two
  // insights for one reveal (fast double-click quota bug).
  const [spendingWords, setSpendingWords] = useState<ReadonlySet<string>>(() => new Set());
  const spendingWordsRef = useRef<Set<string>>(new Set());

  const handleReveal = useCallback((word: string) => {
    if (spendingWordsRef.current.has(word)) return;
    const data = insightFor(word, words);
    if (!data) return;                    // spec §3.7: no data, no charge
    // Presence, not truthiness: a `null` entry is still an already-paid
    // word (its chunk just failed to load at boot), and must never be
    // charged again just because this render finally has real data for it.
    if (Object.prototype.hasOwnProperty.call(revealedInsights, word)) return;
    // I1: gate total in-flight spends against the authoritative remaining
    // quota via a ref, not the (possibly one-render-stale) `quotas` prop.
    // Two same-tick clicks on two DIFFERENT words both see the same
    // `quotas.insightsLeft` snapshot — without this, both could pass an
    // `insightsLeft > 0` check and optimistically reveal, even though
    // AppContext's spend queue can only ever honor one of them, leaving
    // the second word's definition shown for free.
    if (spendingWordsRef.current.size >= quotasRef.current.insightsLeft) return;
    spendingWordsRef.current.add(word);
    setSpendingWords(new Set(spendingWordsRef.current));
    setRevealedInsights((current) => {
      const next = { ...current, [word]: data };
      // I4: persist immediately so a remount can't lose the reveal or
      // re-charge for the same word later in this game.
      void saveSession(
        storage,
        toSession(id, view.game, result, Object.keys(next)),
        language,
      ).catch(() => {});
      return next;
    });
    void spendInsight().catch(() => {}).finally(() => {
      spendingWordsRef.current.delete(word);
      setSpendingWords(new Set(spendingWordsRef.current));
    });
  }, [revealedInsights, spendInsight, storage, id, view.game, result, language, words]);

  const danceRow = view.finished && view.game.status === 'won'
    ? view.game.guesses.length - 1
    : null;

  const sheetOpen = insightOpen || (resultOpen && result !== null);

  return (
    <div className="flex w-full min-h-0 flex-1 flex-col gap-1 sm:gap-3">
      {banner({ finished: view.finished, openResult })}

      <HintBanner answer={view.game.answer} revealed={view.game.revealedHints} />

      <div className="relative min-h-0 flex-1">
        <Toast toast={view.toast} />
        <Board
          answer={view.game.answer}
          guesses={view.game.guesses}
          draft={view.draft}
          revealingRow={view.revealingRow}
          shaking={view.shaking}
          danceRow={danceRow}
        />
      </div>

      {result ? (
        <ResultSheet
          open={resultOpen}
          onClose={() => setResultOpen(false)}
          mode={view.game.mode}
          answer={view.game.answer}
          result={result}
          shareId={shareId}
          answerLanguage={words}
          onShare={handleShare}
          ctaLabel={ctaLabel}
          onCta={onCta}
          footer={resultFooter}
          banner={resultBanner}
        />
      ) : null}

      <Keyboard
        keys={keys}
        onLetter={typeLetter}
        onBackspace={backspace}
        onEnter={submit}
        disabled={view.game.status !== 'playing' || sheetOpen || view.revealingRow !== null}
      />
      <FooterActions
        hintsLeft={quotas.hintsLeft}
        insightsLeft={quotas.insightsLeft}
        hintDisabled={view.game.status !== 'playing' || hintSpending || view.revealingRow !== null}
        revealedInsightCount={Object.keys(revealedInsights).length}
        onHint={handleHint}
        onInsight={() => setInsightOpen(true)}
        onLogo={openMenu}
      />
      <InsightSheet
        open={insightOpen}
        onClose={() => setInsightOpen(false)}
        words={guessedWords}
        insightsLeft={quotas.insightsLeft}
        revealed={revealedInsights}
        onReveal={handleReveal}
        spendingWords={spendingWords}
        lookup={(word) => insightFor(word, words)}
      />
      <p role="status" className="sr-only">
        {view.finished
          ? (view.game.status === 'won'
            ? t.board.solvedIn(view.game.guesses.length)
            : t.board.outOfGuessesWord(view.game.answer.toUpperCase()))
          : t.board.guessOf(view.game.guesses.length + 1, maxGuessesFor(view.game.answer))}
      </p>
      <span className="sr-only">{canType ? '' : t.header.boardLocked}</span>
    </div>
  );
}
